/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';

import { any } from 'prop-types';

import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getOrderId,
    getPaymentMethod,
    getPaymentError,
    disablePaymentFields,
    getBrand,
} from '../../../../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import {
    getCartCount,
    getCartOrder,
    getGiftCards,
    getOrderTotalBalance,
} from '../../../../../../../../../state/ducks/MfeCheckout/ducks/Cart/Cart-Selectors';
import { paymentPlaceOrder } from '../../../../../../../../../state/ducks/MfeCheckout/ducks/Payment/PlaceOrder/PlaceOrder-Actions';

import { anchorToSection, isPaymentOrReviewPage } from '../../../Helpers/Helper';
import usePrevious from '../../../../../../../../gql/hooks/usePrevious';
import { clearOrderItemErrors, setOrderItemError } from '../../../../../../../../../state/ducks/Checkout/ducks/Common/ducks/Error/Error-Actions';
import useBrowserUUID from '../../../../../../../../helpers/useBrowserUUID';
import { getCountryCodeFromThreeToTwoDigit } from '../../../../../../../../../state/ducks/Checkout/helpers/countryCode';
import { getFeatureFlags, getOnePageCheckoutSaleForceABTest } from '../../../../../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';

const initialContext = {
    pazeInitializeResponse: {},
    authorizePayment: {},
    isReady: false,
    setReady: () => {},
    isScriptLoaded: false,
    setScriptLoaded: () => {},
};

const PazeContext = createContext(initialContext);
function PazeProvider({ children }) {
    const dispatchAction = useDispatch();

    const [isScriptLoaded, setScriptLoaded] = useState(false);
    const [isReady, setReady] = useState(false);
    const [pazeInitializeResponse, setPazeInitializeResponse] = useState({});

    const appliedGiftCards = useSelector(getGiftCards);
    const cartCount = useSelector(getCartCount);
    const order = useSelector(getCartOrder);
    const orderTotal = useSelector(getOrderTotalBalance);
    const selectedPaymentMethod = useSelector(getPaymentMethod);
    const brand = useSelector(getBrand);
    const isDisablePaymentFields = useSelector(disablePaymentFields);
    const cartId = useSelector(getOrderId);
    const paymentError = useSelector(getPaymentError);

    const featureFlags = useSelector(getFeatureFlags) || {};
    const onePageCheckout = useSelector(getOnePageCheckoutSaleForceABTest) || {};

    const prevSelectedPaymentMethod = usePrevious(selectedPaymentMethod);
    const prevOrderTotal = usePrevious(orderTotal);

    const history = useHistory();

    let isPaymentPage = history?.location?.pathname ? isPaymentOrReviewPage(history.location.pathname) : false;

    if (
        history?.location?.pathname?.indexOf('/shipping') > 0
        && (
            featureFlags['is-one-page-checkout-enabled'] === 'newflow'
            || (
                featureFlags['is-one-page-checkout-enabled'] === 'salesforce'
                && onePageCheckout?.campaign?.campaignResponses?.[0]?.userGroup === 'Default'
            )
        )
    ) {
        isPaymentPage = true;
    }

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (window.sessionStorage?.setItem) {
                const storedValue = window.sessionStorage.getItem('payment:pazeSession');
                if (storedValue) {
                    setPazeInitializeResponse(JSON.parse(storedValue));
                }
            }

            if (document.querySelector('#paze-payment-script') && !isScriptLoaded) {
                setScriptLoaded(true);
            }
        }
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.sessionStorage?.setItem && pazeInitializeResponse) {
            const storedValue = window.sessionStorage.getItem('payment:pazeSession');

            if (storedValue !== JSON.stringify(pazeInitializeResponse)) {
                window.sessionStorage.setItem('payment:pazeSession', JSON.stringify(pazeInitializeResponse));
            }

            if (pazeInitializeResponse === 'success' && !isReady) {
                setReady(true);
            }
        }
    }, [pazeInitializeResponse]);

    useEffect(() => {
        if (
            prevSelectedPaymentMethod
            && selectedPaymentMethod.id
            && prevSelectedPaymentMethod.id !== selectedPaymentMethod.id
            && selectedPaymentMethod.id === 'Paze'
        ) {
            dispatchAction(
                clearOrderItemErrors(
                    cartId,
                    'payment',
                    {
                        errType: 'clearAllFields',
                        field: '',
                    },
                ),
            );
        }
    }, [selectedPaymentMethod]);

    async function fetchPazeInitialize() {
        try {
            const payload = {
                client: {
                    id: mbpUtil.getEnv('APP_PAZE_ID'),
                    name: brand?.['display-name']?.replace('\'', ''),
                    profileId: '1800Flowers',

                },
            };

            setReady(true);
            const response = await window.DIGITAL_WALLET_SDK.initialize(payload);
            setPazeInitializeResponse('success');
        } catch (ex) {
            setPazeInitializeResponse({});

            mbpLogger.logError({
                function: 'fetchPazeInitialize',
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                message: `failed fetchPazeInitialize paze ${cartId}`,
                jsError: ex,
                orderId: cartId,
            });

            dispatchAction(
                setOrderItemError(
                    cartId,
                    {
                        payment: {
                            errorKey: 'ERROR_PAZE_PAYMENT_INITIALIZE',
                            errorMessage: 'We are sorry, there seems to be a technical issue with Paze payment method, please try again later or use alternate payment method to complete order',
                        },
                    },
                ),
            );
            anchorToSection('span[class*="ErrorMessage-message-"]');
        }
    }

    useEffect(() => {
        // console.log('init - isReady', isReady);
        // console.log('init - isScriptLoaded', isScriptLoaded);
        // console.log('init - isPaymentPage', isPaymentPage);
        // console.log('init - selectedPaymentMethod?.id', selectedPaymentMethod?.id);
        // console.log('init - prevSelectedPaymentMethod.id', prevSelectedPaymentMethod.id);
        // console.log('init - orderTotal', orderTotal);
        // console.log('init  if', pazeInitializeResponse !== 'success',
        //      selectedPaymentMethod?.id !== prevSelectedPaymentMethod?.id,
        //      (pazeInitializeResponse === 'success' && orderTotal !== prevOrderTotal));
        if (
            isScriptLoaded
            && isPaymentPage
            && selectedPaymentMethod?.id === 'Paze'
            && !isReady
            && window.DIGITAL_WALLET_SDK
            && (
                pazeInitializeResponse !== 'success'
                || selectedPaymentMethod.id !== prevSelectedPaymentMethod?.id
                || (pazeInitializeResponse === 'success' && orderTotal !== prevOrderTotal)
            )
        ) {
            fetchPazeInitialize();
        }
    },
    [
        pazeInitializeResponse,
        selectedPaymentMethod,
        orderTotal,
        prevOrderTotal,
        isPaymentPage,
        isScriptLoaded,
    ]);

    useEffect(() => {
        if (isPaymentPage && selectedPaymentMethod?.id === 'Paze' && paymentError?.error) {
            setPazeInitializeResponse({});
        }
    }, [isPaymentPage, paymentError, selectedPaymentMethod]);

    const handleAuthorizationFailure = (response) => {
        dispatchAction(
            setOrderItemError(
                cartId,
                {
                    payment: {
                        errorKey: 'ERROR_PAZE_PAYMENT_AUTHORIZATION',
                        errorMessage: response.message,
                    },
                },
            ),
        );
        anchorToSection('span[class*="ErrorMessage-message-"]');
    };

    const authorizePayment = async () => {
        try {
            if (typeof window !== 'undefined' && window.DIGITAL_WALLET_SDK) {
                const browserUUID = useBrowserUUID();

                const payload = {
                    sessionId: browserUUID,
                    shippingPreference: 'NONE',
                    billingPreference: 'ALL',
                    transactionValue: {
                        transactionCurrencyCode: 'USD',
                        transactionAmount: orderTotal,
                    },
                    intent: 'EXPRESS_CHECKOUT',
                };

                const response = await window.DIGITAL_WALLET_SDK.checkout(payload);
                if (response.result === 'COMPLETE' && response.checkoutResponse) {
                    let country = order.recipients[0].recipientAddress.country;
                    if (country.length === 3) {
                        country = getCountryCodeFromThreeToTwoDigit(order.recipients[0].recipientAddress.country);
                    }

                    const payloadComplete = {
                        sessionId: browserUUID,
                        transactionValue: {
                            transactionCurrencyCode: 'USD',
                            transactionAmount: orderTotal,
                        },
                        transactionOptions: {
                            billingPreference: 'NONE',
                            merchantCategoryCode: 'US',
                            payloadTypeIndicator: 'PAYMENT',
                        },
                        transactionType: 'BOTH',
                        enhancedTransactionData: {
                            ecomData: {
                                cartContainsGiftCard: appliedGiftCards.length > 0,
                                orderForPickup: false,
                                orderQuantity: String(cartCount),
                                orderHighestCost: String(order.recipients[0].cartItems[0].itemInfo.price),
                                finalShippingAddress: {
                                    name: `${order.recipients[0].recipientAddress.firstName} ${order.recipients[0].recipientAddress.lastName}`,
                                    line1: order.recipients[0].recipientAddress.address1,
                                    line2: order.recipients[0].recipientAddress.address2,
                                    city: order.recipients[0].recipientAddress.city,
                                    state: order.recipients[0].recipientAddress.state,
                                    zip: order.recipients[0].recipientAddress.zipCode,
                                    countryCode: country,
                                },
                            },
                        },
                    };

                    const completeResponse = await window.DIGITAL_WALLET_SDK.complete(payloadComplete);

                    if (completeResponse.completeResponse) {
                        const placeOrderConfig = {
                            history,
                            pazePayment: {
                                authorizeCode: completeResponse.completeResponse,
                            },
                            disablePaymentFields: isDisablePaymentFields,
                            source: 'PWA',
                        };
                        dispatchAction(paymentPlaceOrder(placeOrderConfig));
                    } else {
                        mbpLogger.logError({
                            function: 'window.DIGITAL_WALLET_SDK.complete',
                            appName: process.env.npm_package_name,
                            module: 'mbp-checkout',
                            message: `failed to complete paze payment ${cartId}`,
                            jsError: response,
                            orderId: cartId,
                        });

                        handleAuthorizationFailure(response);
                    }
                } else {
                    // Paze team advised to redirect back to payment page if user exits flow prematurely
                    // TODO: Remove this when Paze team review and approved.
                    mbpLogger.logError({
                        function: 'window.DIGITAL_WALLET_SDK.checkout',
                        appName: process.env.npm_package_name,
                        module: 'mbp-checkout',
                        message: `failed or cancel to checkout paze payment ${cartId}`,
                        jsError: response,
                        orderId: cartId,
                    });

                    // Re-starting paze when modal is close or something goes wrong on paze.
                    fetchPazeInitialize();
                }
            } else {
                mbpLogger.logError({
                    function: 'authorizePayment',
                    appName: process.env.npm_package_name,
                    module: 'mbp-checkout',
                    message: `failed authorizePayment paze ${cartId}`,
                    jsError: 'window.DIGITAL_WALLET_SDK is not initialized',
                    orderId: cartId,
                });
            }
        } catch (ex) {
            console.log(ex);
            mbpLogger.logError({
                function: 'authorizePayment',
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                message: `failed authorizePayment paze ${cartId}`,
                jsError: ex,
                orderId: cartId,
            });
            handleAuthorizationFailure(ex);
        }
    };

    return (
        <PazeContext.Provider
            value={{
                pazeInitializeResponse,
                authorizePayment,
                isReady,
                setReady,
                isScriptLoaded,
                setScriptLoaded,
            }}
        >
            {children}
        </PazeContext.Provider>
    );
}

PazeProvider.propTypes = {
    children: any.isRequired,
};

function usePazeContext() {
    const context = useContext(PazeContext);

    return context;
}

export {
    PazeProvider,
    usePazeContext,
};
